/**
 * เอกสารตามอายุงาน — 2 แท็บในหน้าเดียว
 *   "ถึงกำหนด"  = รายการที่ต้องจัดการวันนี้ เรียงด่วนก่อน
 *   "ตั้งค่ากฎ" = HR เพิ่ม/แก้/ลบกฎเองได้ทั้งหมด ไม่มีตัวเลขวันไหน hardcode ในระบบ
 *
 * ⭐ กฎ 1 ข้อ = "นับจากอะไร + บวก/ลบกี่วัน + เตือนล่วงหน้ากี่วัน"
 */
import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useToast } from '../../components/ui/toast';
import {
  useDocumentRules, useSaveDocumentRules, useDocumentRulesDue, useMarkDocumentRule, useContractSigningSummary,
  type DocumentRuleRow, type DocumentRuleDueItem,
} from '../../hooks';
import { DOC_RULE_ANCHORS, DOC_RULE_ANCHOR_LABELS, EMPLOYEE_DOC_TYPES, EMPLOYEE_DOC_TYPE_LABELS, type EmployeeDocType } from '@payroll/shared';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Check, AlertTriangle, Clock, FileSignature } from 'lucide-react';

function stateChip(state: DocumentRuleDueItem['state']): string {
  return state === 'overdue' ? 'bg-red-100 text-red-700'
    : state === 'due_today' ? 'bg-orange-100 text-orange-800'
      : 'bg-amber-100 text-amber-800';
}
function stateText(it: DocumentRuleDueItem): string {
  if (it.state === 'overdue') return `เลยกำหนด ${Math.abs(it.days_left ?? 0)} วัน`;
  if (it.state === 'due_today') return 'ครบกำหนดวันนี้';
  return `เหลือ ${it.days_left} วัน`;
}

export function HRDocumentRules() {
  const [tab, setTab] = useState<'due' | 'rules'>('due');
  return (
    <AdminLayout title="เอกสารตามอายุงาน">
      <div className="flex gap-1 mb-3 border-b border-gray-200">
        {([['due', 'ถึงกำหนด'], ['rules', 'ตั้งค่ากฎ']] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500'}`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'due' ? <DueTab /> : <RulesTab />}
    </AdminLayout>
  );
}

// ═══════════════ แท็บ "ถึงกำหนด" ═══════════════
function DueTab() {
  const { data, isLoading } = useDocumentRulesDue();
  const mark = useMarkDocumentRule();
  const toast = useToast();

  async function handleDone(it: DocumentRuleDueItem) {
    try {
      await mark.mutateAsync({ emp_code: it.emp_code, rule_key: it.rule_key, cycle_key: it.cycle_key, done: true });
      toast.success(`บันทึกว่าจัดการ "${it.rule_label}" ของ ${it.name} แล้ว`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  const s = data?.summary;
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        รายการที่ถึงกำหนดต้องจัดการ — คำนวณจากวันเริ่มงาน วันครบทดลองงาน และวันสิ้นสุดสัญญาของแต่ละคน
      </p>

      <div className="grid grid-cols-3 gap-2">
        <div className="card p-3">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
            <AlertTriangle className="w-3.5 h-3.5" /><span className="whitespace-nowrap">เลยกำหนด</span>
          </div>
          <div className="text-2xl font-semibold leading-none text-red-700">{s?.overdue ?? 0}</div>
        </div>
        <div className="card p-3">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
            <Clock className="w-3.5 h-3.5" /><span className="whitespace-nowrap">ครบวันนี้</span>
          </div>
          <div className="text-2xl font-semibold leading-none text-orange-700">{s?.due_today ?? 0}</div>
        </div>
        <div className="card p-3">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
            <Clock className="w-3.5 h-3.5" /><span className="whitespace-nowrap">ใกล้ครบ</span>
          </div>
          <div className="text-2xl font-semibold leading-none text-amber-700">{s?.due_soon ?? 0}</div>
        </div>
      </div>

      {(data?.missing_info?.length ?? 0) > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
          <div className="font-semibold mb-1">
            {data!.missing_info.length} คนยังคำนวณกำหนดให้ไม่ได้ — ข้อมูลไม่ครบ
          </div>
          <div className="space-y-0.5">
            {data!.missing_info.slice(0, 8).map((m) => (
              <div key={m.emp_code}>{m.name} <span className="text-amber-700">— ขาด {m.missing.join(' · ')}</span></div>
            ))}
            {data!.missing_info.length > 8 && <div>และอีก {data!.missing_info.length - 8} คน</div>}
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-400">กำลังโหลด…</p>
      ) : (data?.items?.length ?? 0) === 0 ? (
        <p className="text-sm text-gray-400 italic">ไม่มีรายการที่ถึงกำหนดตอนนี้</p>
      ) : (
        <div className="space-y-2">
          {data!.items.map((it) => (
            <div key={`${it.emp_code}|${it.rule_key}|${it.cycle_key}`} className="card p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  {it.rule_label}
                  <span className={`inline-block text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap font-normal ${stateChip(it.state)}`}>
                    {stateText(it)}
                  </span>
                  {!it.required && <span className="text-[10px] text-gray-400">(ไม่บังคับ)</span>}
                </div>
                <div className="text-sm text-gray-700 mt-0.5">{it.name}</div>
                <div className="text-xs text-gray-400">
                  {it.emp_code}{it.department ? ` · ${it.department}` : ''}
                  {it.due_date ? ` · ครบกำหนด ${it.due_date}` : ''}
                  {it.doc_label ? ` · ออก${it.doc_label}` : ''}
                </div>
                {it.note && <div className="text-[11px] text-gray-500 mt-1">{it.note}</div>}
              </div>
              <button
                onClick={() => void handleDone(it)}
                disabled={mark.isPending}
                className="btn-secondary btn-slim whitespace-nowrap flex-shrink-0"
              >
                <Check className="w-3.5 h-3.5" /> จัดการแล้ว
              </button>
            </div>
          ))}
        </div>
      )}

      <ContractSigningSection />
    </div>
  );
}

// ═══════════════ สถานะเซ็นสัญญาตามประเภท (บริการ 1 เดือน / ทดลองงาน / พนักงานประจำ) ═══════════════
function ContractSigningSection() {
  const { data, isLoading, isError, refetch } = useContractSigningSummary();
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div>
      <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
        <FileSignature className="w-4 h-4" /> สถานะเซ็นสัญญาตามประเภท
      </div>

      {isLoading ? (
        <p className="text-xs text-gray-400">กำลังโหลด…</p>
      ) : isError ? (
        <p className="text-xs text-red-600">
          โหลดสถานะเซ็นสัญญาไม่สำเร็จ{' '}
          <button type="button" onClick={() => void refetch()} className="underline">ลองใหม่</button>
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {(data?.types ?? []).map((t) => (
              <button
                key={t.doc_type}
                type="button"
                onClick={() => setExpanded(expanded === t.doc_type ? null : t.doc_type)}
                className={`card p-3 text-left w-full ${expanded === t.doc_type ? 'ring-2 ring-brand-500' : ''}`}
              >
                <div className="text-[11px] text-gray-500 mb-1">{t.label}</div>
                <div className="text-[11px] text-amber-700 whitespace-nowrap">ยังไม่ได้เซ็น</div>
                <div className="text-2xl font-semibold leading-none text-amber-700">{t.unsigned_count}</div>
                <div className="text-[11px] text-gray-400 mt-1 whitespace-nowrap">
                  เซ็นแล้ว {t.signed_count} · ทั้งหมด {t.signed_count + t.unsigned_count} คน
                </div>
              </button>
            ))}
          </div>

          {expanded && (
            <ContractSigningDetail
              type={(data?.types ?? []).find((t) => t.doc_type === expanded) ?? null}
            />
          )}
        </>
      )}
    </div>
  );
}

function ContractSigningDetail({ type }: { type: NonNullable<ReturnType<typeof useContractSigningSummary>['data']>['types'][number] | null }) {
  if (!type) return null;
  return (
    <div className="card p-3 mt-2">
      <div className="text-xs font-semibold text-gray-600 mb-2">{type.label} — รายชื่อที่ยังไม่ได้เซ็น</div>
      {type.unsigned.length === 0 ? (
        <p className="text-xs text-gray-400 italic">ทุกคนเซ็นครบแล้ว</p>
      ) : (
        <div className="space-y-1.5">
          {type.unsigned.map((u) => (
            <div key={u.emp_code} className="flex items-center justify-between gap-2 border-b border-gray-100 last:border-0 pb-1.5 last:pb-0">
              <div className="min-w-0">
                <div className="text-sm truncate">{u.name}</div>
                <div className="text-[11px] text-gray-400">{u.emp_code}{u.department ? ` · ${u.department}` : ''}</div>
              </div>
              <span
                className={`inline-block text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap flex-shrink-0 ${
                  u.state === 'awaiting' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {u.state === 'awaiting' ? 'รอเซ็น' : 'ยังไม่มีเอกสาร'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════ แท็บ "ตั้งค่ากฎ" ═══════════════
function RulesTab() {
  const { data, isLoading } = useDocumentRules();
  const save = useSaveDocumentRules();
  const toast = useToast();

  const [rules, setRules] = useState<DocumentRuleRow[]>([]);
  const [probationDefault, setProbationDefault] = useState<string>('');

  useEffect(() => {
    if (!data) return;
    setRules(data.rules.map((r) => ({ ...r })));
    setProbationDefault(data.probation_days_default != null ? String(data.probation_days_default) : '');
  }, [data]);

  function update(idx: number, patch: Partial<DocumentRuleRow>) {
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function move(idx: number, dir: -1 | 1) {
    setRules((prev) => {
      const t = idx + dir;
      if (t < 0 || t >= prev.length) return prev;
      const next = [...prev];
      const a = next[idx]!; next[idx] = next[t]!; next[t] = a;
      return next.map((r, i) => ({ ...r, sort_order: (i + 1) * 10 }));
    });
  }
  function add() {
    setRules((prev) => [...prev, {
      rule_key: `rule_${Date.now()}`,
      label: '',
      doc_type: null,
      anchor: 'start_date',
      offset_days: 0,
      warn_days: 14,
      required: true,
      active: true,
      sort_order: (prev.length + 1) * 10,
      note: null,
    }]);
  }

  async function handleSave() {
    const cleaned = rules.filter((r) => r.label.trim()).map((r, i) => ({ ...r, sort_order: (i + 1) * 10 }));
    const keys = new Set<string>();
    for (const r of cleaned) {
      if (keys.has(r.rule_key)) { toast.error(`รหัสกฎซ้ำ: ${r.rule_key}`); return; }
      keys.add(r.rule_key);
    }
    const pd = probationDefault.trim() === '' ? null : Number(probationDefault);
    if (pd !== null && (!Number.isInteger(pd) || pd <= 0 || pd > 365)) {
      toast.error('จำนวนวันทดลองงานต้องอยู่ระหว่าง 1-365 วัน');
      return;
    }
    try {
      await save.mutateAsync({ rules: cleaned, probation_days_default: pd });
      toast.success('บันทึกแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        ตั้งเองได้ทุกอย่าง — จะเตือนเรื่องอะไร นับจากวันไหน ล่วงหน้ากี่วัน ระบบไม่ได้ฝังตัวเลขไว้เอง
      </p>

      <div className="card p-3">
        <div className="font-medium text-sm mb-2">ค่าเริ่มต้นของบริษัท</div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 whitespace-nowrap">ทดลองงาน</span>
          <input
            type="number" min={1} max={365}
            value={probationDefault}
            onChange={(e) => setProbationDefault(e.target.value)}
            placeholder="—"
            className="input w-28"
          />
          <span className="text-sm text-gray-600 whitespace-nowrap">วัน</span>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          ใช้กับทุกคนที่ไม่ได้ตั้งค่าเฉพาะตัวไว้ · ตั้งรายคนได้ที่ทะเบียนพนักงาน แท็บ "งาน"
        </p>
        <p className="text-xs text-amber-700 mt-1">
          ข้อควรรู้ตามกฎหมาย: ถ้าทำงานครบ 120 วันแล้วเลิกจ้าง ต้องจ่ายค่าชดเชย — หลายบริษัทจึงตั้งทดลองงานไว้ 119 วัน
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">กำลังโหลด…</p>
      ) : (
        <div className="space-y-2">
          {rules.map((r, idx) => (
            <RuleRow
              key={r.rule_key}
              rule={r}
              onChange={(p) => update(idx, p)}
              onRemove={() => setRules((prev) => prev.filter((_, i) => i !== idx))}
              onUp={idx > 0 ? () => move(idx, -1) : undefined}
              onDown={idx < rules.length - 1 ? () => move(idx, 1) : undefined}
            />
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={add} className="btn-secondary flex-1"><Plus className="w-4 h-4" /> เพิ่มกฎ</button>
        <button onClick={() => void handleSave()} disabled={save.isPending} className="btn-cta flex-1">
          <Save className="w-4 h-4" /> {save.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
      </div>
    </div>
  );
}

function RuleRow({ rule, onChange, onRemove, onUp, onDown }: {
  rule: DocumentRuleRow;
  onChange: (p: Partial<DocumentRuleRow>) => void;
  onRemove: () => void;
  onUp?: () => void;
  onDown?: () => void;
}) {
  const offsetWord = rule.offset_days === 0 ? 'วันนั้นพอดี'
    : rule.offset_days < 0 ? `ก่อนถึง ${Math.abs(rule.offset_days)} วัน`
      : `หลังจากนั้น ${rule.offset_days} วัน`;

  return (
    <div className="card p-3 space-y-2">
      <div className="flex items-center gap-2">
        <input
          value={rule.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="ชื่อกฎ เช่น ประเมินผลทดลองงาน"
          className="input flex-1"
        />
        <button onClick={onUp} disabled={!onUp} className="btn-secondary px-2 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
        <button onClick={onDown} disabled={!onDown} className="btn-secondary px-2 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
        <button onClick={onRemove} className="btn-secondary px-2 text-red-600"><Trash2 className="w-4 h-4" /></button>
      </div>

      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span className="text-gray-600 whitespace-nowrap">นับจาก</span>
        <select
          value={rule.anchor}
          onChange={(e) => onChange({ anchor: e.target.value as DocumentRuleRow['anchor'] })}
          className="input w-auto"
        >
          {DOC_RULE_ANCHORS.map((a) => (
            <option key={a} value={a}>{DOC_RULE_ANCHOR_LABELS[a]}</option>
          ))}
        </select>
        <input
          type="number" min={-365} max={365}
          value={rule.offset_days}
          onChange={(e) => onChange({ offset_days: Math.max(-365, Math.min(365, Number(e.target.value) || 0)) })}
          className="input w-24"
        />
        <span className="text-gray-500 text-xs whitespace-nowrap">วัน ({offsetWord})</span>
      </div>

      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span className="text-gray-600 whitespace-nowrap">ขึ้นเตือนล่วงหน้า</span>
        <input
          type="number" min={0} max={365}
          value={rule.warn_days}
          onChange={(e) => onChange({ warn_days: Math.max(0, Math.min(365, Number(e.target.value) || 0)) })}
          className="input w-24"
        />
        <span className="text-gray-600 whitespace-nowrap">วัน</span>
        <label className="flex items-center gap-2 px-2">
          <input type="checkbox" checked={rule.required} onChange={(e) => onChange({ required: e.target.checked })} className="w-4 h-4" />
          บังคับ
        </label>
      </div>

      <select
        value={rule.doc_type ?? ''}
        onChange={(e) => onChange({ doc_type: e.target.value || null })}
        className="input w-full"
      >
        <option value="">ไม่ต้องออกเอกสาร (แค่เตือนให้ทำ)</option>
        {EMPLOYEE_DOC_TYPES.map((t) => (
          <option key={t} value={t}>ต้องออก{EMPLOYEE_DOC_TYPE_LABELS[t as EmployeeDocType]}</option>
        ))}
      </select>

      <input
        value={rule.note ?? ''}
        onChange={(e) => onChange({ note: e.target.value || null })}
        placeholder="หมายเหตุ (ไม่บังคับ) — จะแสดงในรายการที่ถึงกำหนด"
        className="input w-full"
      />
    </div>
  );
}
